import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, comics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredComics = useMemo(() => {
    if (!category) return [];
    let result = comics;

    // If not a specific category like hot/latest/finished, filter by category
    if (['guoman', 'riman', 'hanman'].includes(category.slug)) {
      result = result.filter((c) => c.category === category.slug);
    } else if (category.slug === 'hot') {
      result = result.filter((c) => c.hot);
    } else if (category.slug === 'finished') {
      result = result.filter((c) => c.status === '已完结');
    }
    // latest - show all sorted by date

    // Subcategory filter
    if (activeSub !== 'all') {
      result = result.filter((c) => c.subcategory === activeSub);
    }

    // Sort
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [category, activeSub, sortBy]);

  useEffect(() => {
    setActiveSub('all');
    setSortBy('popular');

    if (category) {
      document.title = `${category.name} - 琉璃漫站 | 正版漫画在线阅读`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `琉璃漫站${category.name}专区，${category.description}。海量${category.name}资源，高清流畅阅读体验，尽在琉璃漫站。`);
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute('content', `琉璃漫站,${category.name},${category.name}推荐,漫画在线阅读,${category.name}漫画`);
    }
  }, [slug, category]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-600">分类不存在</p>
      </div>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 琉璃漫站`,
    description: category.description,
    url: `https://liulicomic.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '琉璃漫站',
      url: 'https://liulicomic.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              {category.name}
            </h1>
            <p className="text-foreground-600 text-base">{category.description}</p>
          </div>

          {/* Filters & Sort */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            {/* Subcategory Tabs */}
            {category.subcategories.length > 0 && (
              <div className="flex items-center flex-wrap gap-2">
                <button
                  onClick={() => setActiveSub('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSub === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </button>
                {category.subcategories.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSub(sub)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeSub === sub
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'popular' as const, label: '最热' },
                { key: 'latest' as const, label: '最新' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5 pb-16">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group"
                data-product-shop
              >
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 琉璃漫站正版漫画`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-foreground-700 text-background-50'
                  }`}>
                    {comic.status}
                  </div>
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-xs">
                    <i className="ri-star-fill text-accent-400 text-xs"></i>
                    {comic.rating}
                  </div>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-foreground-500">{comic.author}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500">{comic.subcategory}</span>
                </div>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500">暂无相关漫画</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}